/** @jsxImportSource @emotion/react */
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AdyenAccountHolderListItem } from "../models/adyen-account-holder";
import { Table } from "../../../components/table-components/Table";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableBody } from "../../../components/table-components/TableBody";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { TableCellLink } from "../../../components/table-components/TableCellLink";
import { StyledText } from "../../../assets/typography/typography";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { DropdownItemLink } from "../../common/DropdownItemLink";
import { colors } from "../../../components/colors";
import { TableRow } from "../../../components/table-components/TableRow";
import { routes } from "../../../appRoutingHelpers";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { usePushAdyenAccountHolderToAdyen } from "../service/adyen-account-holders-api";
import { ButtonPrimary } from "../../../components/NewButtons";
import { getErrorMessage } from "../../../error-handler";
import { toastr } from "react-redux-toastr";
import { SpinnerBlock } from "../../common/Spinner";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { ActionSpinner } from "../../../components/ActionSpinner";

type Props = {
  accountHolders: AdyenAccountHolderListItem[];
};

export const AdyenAccountHoldersTable = ({ accountHolders }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    mutateAsync: pushAdyenAccountHolderToAdyen,
    isLoading: isPushingAdyenAccountHolderToAdyen,
  } = usePushAdyenAccountHolderToAdyen();

  const handlePushAdyenAccountHolderToAdyen = async (id: string) => {
    try {
      await pushAdyenAccountHolderToAdyen({ data: { id } });
      toastr.success("Success", "Adyen account holder pushed to Adyen");
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  return (
    <Table>
      <thead>
        <TableRowHeader>
          <TableCellHeaderText>Adyen reference</TableCellHeaderText>
          <TableCellHeaderText>Description</TableCellHeaderText>
          <TableCellHeaderText>Organization</TableCellHeaderText>
          <th />
        </TableRowHeader>
      </thead>
      <TableBody>
        {accountHolders.map((ah, i) => {
          const href = routes.adyenAccountHolder(organizationKeyName, ah.id);

          return (
            <TableRow key={i}>
              {ah.reference ? (
                <TableCellLink href={href} className="pl-5">
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {ah.reference}
                  </StyledText>
                </TableCellLink>
              ) : (
                <TableCellText>
                  <ButtonPrimary
                    disabled={isPushingAdyenAccountHolderToAdyen}
                    onClick={() => handlePushAdyenAccountHolderToAdyen(ah.id)}
                  >
                    {isPushingAdyenAccountHolderToAdyen ? (
                      <SpinnerBlock />
                    ) : (
                      "Push to Adyen"
                    )}
                  </ButtonPrimary>
                </TableCellText>
              )}

              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {ah.description}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {ah.organizationName || "-"}
                </StyledText>
              </TableCellLink>
              <TableEllipsisDropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <DropdownItemLink
                          icon={<FontAwesomeIcon icon={solid("pen")} />}
                          to={href}
                        >
                          Edit
                        </DropdownItemLink>
                      ),
                    },
                    {
                      key: "2",
                      label: (
                        <DropdownItemButton
                          icon={
                            isPushingAdyenAccountHolderToAdyen ? (
                              <ActionSpinner />
                            ) : (
                              <FontAwesomeIcon icon={solid("upload")} />
                            )
                          }
                          disabled={isPushingAdyenAccountHolderToAdyen}
                          onClick={() =>
                            handlePushAdyenAccountHolderToAdyen(ah.id)
                          }
                        >
                          Push to Adyen
                        </DropdownItemButton>
                      ),
                    },
                  ],
                }}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
